import { Component, lazy as reactLazy, Suspense, type ComponentType, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";

// Preserve search params when redirecting /solar-rescue/system-takeover → hub anchor.
const TakeoverRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/solar-rescue${search}#system-takeover`} replace />;
};
import ScrollToTop from "./components/ScrollToTop.tsx";
import AliasRedirect from "./components/routing/AliasRedirect.tsx";
import RouteProgress from "./components/RouteProgress.tsx";
import { RouteAwareAuthProvider } from "./hooks/RouteAwareAuthProvider.tsx";
import AdminProtectedRoute from "./components/AdminProtectedRoute.tsx";
import { DeferredUIProviders } from "./components/DeferredUIProviders.tsx";
import { useIdleReady } from "./hooks/use-idle-ready.ts";
import ConsentAwareAnalytics from "./components/consent/ConsentAwareAnalytics.tsx";
import PublicSiteSurface from "./components/layout/PublicSiteSurface.tsx";

const ROUTE_CHUNK_RELOAD_KEY = "magic-builders-route-chunk-reloaded";

function isRouteChunkLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  return /Importing a module script failed|Failed to fetch dynamically imported module|Loading chunk|_result\.default/i.test(message);
}

function lazy<T extends { default: ComponentType<unknown> }>(importer: () => Promise<T>) {
  return reactLazy(async () => {
    try {
      const module = await importer();
      if (!module?.default) throw new Error("Lazy route module missing default export (_result.default)");
      return module;
    } catch (error) {
      if (isRouteChunkLoadError(error) && sessionStorage.getItem(ROUTE_CHUNK_RELOAD_KEY) !== "1") {
        sessionStorage.setItem(ROUTE_CHUNK_RELOAD_KEY, "1");
        window.location.reload();
      }
      throw error;
    }
  });
}

class RouteChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(error: unknown) {
    return { hasError: isRouteChunkLoadError(error) };
  }

  componentDidCatch(error: unknown) {
    if (!isRouteChunkLoadError(error)) throw error;
    console.error("[Route] failed to load page module:", error);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="min-h-screen bg-background px-6 py-24 text-foreground">
        <div className="mx-auto max-w-xl space-y-5 text-center">
          <p className="font-heading text-4xl uppercase tracking-normal text-primary">Page update needed</p>
          <p className="text-muted-foreground">We couldn’t load the latest page files. Refresh once to continue.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex h-12 items-center justify-center bg-primary px-6 font-heading text-lg uppercase tracking-normal text-primary-foreground transition-opacity hover:opacity-90"
          >
            Refresh page
          </button>
        </div>
      </main>
    );
  }
}

// Defer non-critical UI (cookie banner, sticky chat widget) — they're invisible
// at first paint and pull in Supabase + Dialog code we don't need on LCP.
const CookieConsent = lazy(() => import("./components/CookieConsent.tsx"));
const LiveTextWidget = lazy(() => import("./components/LiveTextWidget.tsx"));
const QuotePrompt = lazy(() => import("./components/conversion/QuotePrompt.tsx"));

// Lazy-loaded pages (existing)
const Index = lazy(() => import("./pages/Index.tsx"));
const OurWork = lazy(() => import("./pages/OurWork.tsx"));
const Services = lazy(() => import("./pages/Services.tsx"));
const Partners = lazy(() => import("./pages/Partners.tsx"));
const PartnerSalesOrg = lazy(() => import("./pages/PartnerSalesOrg.tsx"));
const PartnerInstaller = lazy(() => import("./pages/PartnerInstaller.tsx"));
const PartnerGC = lazy(() => import("./pages/PartnerGC.tsx"));
const PartnerSalesRep = lazy(() => import("./pages/PartnerSalesRep.tsx"));
// EnergyServices removed — /energy now 301s to /solar (SolarLobby)
const SolarInstallation = lazy(() => import("./pages/SolarInstallation.tsx"));
const HomeSolutions = lazy(() => import("./pages/HomeSolutions.tsx"));
const Roofing = lazy(() => import("./pages/Roofing.tsx"));
const BatteryStorage = lazy(() => import("./pages/BatteryStorage.tsx"));
const Enphase = lazy(() => import("./pages/Enphase.tsx"));
const TeslaEnergy = lazy(() => import("./pages/TeslaEnergy.tsx"));
const RemovalReinstall = lazy(() => import("./pages/RemovalReinstall.tsx"));
const SolarServiceMaintenance = lazy(() => import("./pages/SolarServiceMaintenance.tsx"));
const PanelCleaningQuote = lazy(() => import("./pages/PanelCleaningQuote.tsx"));
const Electrical = lazy(() => import("./pages/Electrical.tsx"));
const MainPanelUpgrades = lazy(() => import("./pages/MainPanelUpgrades.tsx"));
const ElectricalServices = lazy(() => import("./pages/ElectricalServices.tsx"));
const EVChargers = lazy(() => import("./pages/EVChargers.tsx"));
const SmartHome = lazy(() => import("./pages/SmartHome.tsx"));
const WildfireSprinkler = lazy(() => import("./pages/WildfireSprinkler.tsx"));
const GeneralContracting = lazy(() => import("./pages/GeneralContracting.tsx"));
const Commercial = lazy(() => import("./pages/Commercial.tsx"));
const CommercialSolar = lazy(() => import("./pages/CommercialSolar.tsx"));
const DirectPaySolar = lazy(() => import("./pages/DirectPaySolar.tsx"));
const CommercialRoofing = lazy(() => import("./pages/CommercialRoofing.tsx"));
const CommercialEVCharging = lazy(() => import("./pages/CommercialEVCharging.tsx"));
const RoofRepairs = lazy(() => import("./pages/RoofRepairs.tsx"));
const GutterCleaning = lazy(() => import("./pages/GutterCleaning.tsx"));
const RoofCleaning = lazy(() => import("./pages/RoofCleaning.tsx"));
const SolarRepair = lazy(() => import("./pages/SolarRepair.tsx"));
// ResidentialRoofing removed — /residential-roofing now 301s to /roofing
const SolarRescue = lazy(() => import("./pages/SolarRescue.tsx"));
import RequestRedirect from "./components/routing/RequestRedirect.tsx";
const SolarRescueConfirmation = lazy(() => import("./pages/SolarRescueConfirmation.tsx"));
const SolarRescueFreedomForever = lazy(() => import("./pages/SolarRescueFreedomForever.tsx"));
const SolarRescueSunPower = lazy(() => import("./pages/SolarRescueSunPower.tsx"));
const SolarRescueSunnova = lazy(() => import("./pages/SolarRescueSunnova.tsx"));
const SolarRescueTitanSolar = lazy(() => import("./pages/SolarRescueTitanSolar.tsx"));
const SolarRescueADTSolar = lazy(() => import("./pages/SolarRescueADTSolar.tsx"));
const SolarRescuePetersenDean = lazy(() => import("./pages/SolarRescuePetersenDean.tsx"));
const InstallerOutOfBusiness = lazy(() => import("./pages/InstallerOutOfBusiness.tsx"));

const SolarCare = lazy(() => import("./pages/SolarCare.tsx"));
const SolarSystemAdders = lazy(() => import("./pages/SolarSystemAdders.tsx"));
const SolarInspections = lazy(() => import("./pages/SolarInspections.tsx"));
const RoofMaintenance = lazy(() => import("./pages/RoofMaintenance.tsx"));
const RoofInspections = lazy(() => import("./pages/RoofInspections.tsx"));
const GetQuote = lazy(() => import("./pages/GetQuote.tsx"));
const BookService = lazy(() => import("./pages/BookService.tsx"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess.tsx"));
const RepairPaymentSuccess = lazy(() => import("./pages/RepairPaymentSuccess.tsx"));
// Retired roof-subtype pages — all routes redirect to /roofing

const RoofingCalculator = lazy(() => import("./pages/RoofingCalculator.tsx"));
const FreeRoofEstimate = lazy(() => import("./pages/FreeRoofEstimate.tsx"));
const SolarBatteryEstimate = lazy(() => import("./pages/SolarBatteryEstimate.tsx"));
const Shop = lazy(() => import("./pages/Shop.tsx"));
const EVCalculatorPage = lazy(() => import("./pages/EVCalculatorPage.tsx"));
const MpuCalculatorPage = lazy(() => import("./pages/MpuCalculatorPage.tsx"));
const ElectricalEstimator = lazy(() => import("./pages/ElectricalEstimator.tsx"));
const SolarCalculator = lazy(() => import("./pages/SolarCalculator.tsx"));
const EnergyDesigner = lazy(() => import("./pages/EnergyDesigner.tsx"));
const RemovalReinstallCalculator = lazy(() => import("./pages/RemovalReinstallCalculator.tsx"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas.tsx"));
const CoverageMap = lazy(() => import("./pages/CoverageMap.tsx"));
const CountyHub = lazy(() => import("./pages/CountyHub.tsx"));
const CityServicePage = lazy(() => import("./pages/CityServicePage.tsx"));
const WildfireRebuild = lazy(() => import("./pages/WildfireRebuild.tsx"));
const EmergencyService = lazy(() => import("./pages/EmergencyService.tsx"));
const ReferralProgram = lazy(() => import("./pages/ReferralProgram.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Financing = lazy(() => import("./pages/Financing.tsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const QuoteView = lazy(() => import("./pages/QuoteView.tsx"));
const RoofQuoteToken = lazy(() => import("./pages/RoofQuoteToken.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const EnergyConstructionAdvisory = lazy(() => import("./pages/EnergyConstructionAdvisory.tsx"));
const SolarLobby = lazy(() => import("./pages/SolarLobby.tsx"));
const SolarIncentivesGuideCalifornia = lazy(() => import("./pages/SolarIncentivesGuideCalifornia.tsx"));
const ConstructionLobby = lazy(() => import("./pages/ConstructionLobby.tsx"));
const AduConstruction = lazy(() => import("./pages/AduConstruction.tsx"));
const RequestConstruction = lazy(() => import("./pages/RequestConstruction.tsx"));
const Visualize = lazy(() => import("./pages/Visualize.tsx"));
const RequestSuccess = lazy(() => import("./pages/RequestSuccess.tsx"));
const HomeAdditions = lazy(() => import("./pages/HomeAdditions.tsx"));
const WholeHomeRemodeling = lazy(() => import("./pages/WholeHomeRemodeling.tsx"));
const NewHomeConstruction = lazy(() => import("./pages/NewHomeConstruction.tsx"));
const QuoteDispatcher = lazy(() => import("./pages/QuoteDispatcher.tsx"));

const PackageDetail = lazy(() => import("./pages/PackageDetail.tsx"));
const PackageBuild = lazy(() => import("./pages/PackageBuild.tsx"));
const Packages = lazy(() => import("./pages/Packages.tsx"));
const SmartElectricalPanels = lazy(() => import("./pages/SmartElectricalPanels.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/Login.tsx"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent.tsx"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard.tsx"));
const AdminClients = lazy(() => import("./pages/admin/Clients.tsx"));
const AdminProjects = lazy(() => import("./pages/admin/Projects.tsx"));
const AdminProjectDetail = lazy(() => import("./pages/admin/ProjectDetail.tsx"));
const AdminInvoices = lazy(() => import("./pages/admin/Invoices.tsx"));
const AdminServiceRequests = lazy(() => import("./pages/admin/ServiceRequests.tsx"));
const AdminRoofColorRequests = lazy(() => import("./pages/admin/RoofColorRequests.tsx"));
const AdminIntakeQueue = lazy(() => import("./pages/admin/IntakeQueue.tsx"));
const AdminVisualizerProject = lazy(() => import("./pages/admin/VisualizerProject.tsx"));
const AdminQuotes = lazy(() => import("./pages/admin/Quotes.tsx"));
const AdminProjectPins = lazy(() => import("./pages/admin/ProjectPins.tsx"));
const AdminHubSpot = lazy(() => import("./pages/admin/HubSpot.tsx"));
const AdminAiIntegrations = lazy(() => import("./pages/admin/AiIntegrations.tsx"));
const AdminAuditLog = lazy(() => import("./pages/admin/AuditLog.tsx"));
const AdminAiPermissions = lazy(() => import("./pages/admin/AiPermissions.tsx"));
const AdminCommandCenter = lazy(() => import("./pages/admin/CommandCenter.tsx"));




const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Match app usage: most reads are dashboard/marketing counters or
      // static-ish lookups. A 5-minute staleTime coalesces the many
      // duplicated hooks on the homepage (see useProjectCount) into one
      // round-trip per key, and refetchOnWindowFocus:false stops the
      // spurious refetches every time the user tabs back.
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Lightweight fallback used while the next route's chunk is downloading.
 * We render `null` instead of a full-screen spinner so the previous page
 * remains visible — `RouteProgress` shows a thin teal progress bar at the
 * very top of the viewport instead. Much smoother than a hard cut.
 */
const PageLoader = () => null;

/**
 * Consent banner mounts independently from the idle-deferred chat widget so
 * it can appear promptly (within ~1s) for visitors who have not yet chosen.
 * Wrapped in Suspense so the lazy chunk still doesn't block first paint.
 */
const ConsentBannerMount = () => (
  <Suspense fallback={null}>
    <CookieConsent />
  </Suspense>
);

/**
 * Defer only the sticky live-text chat widget until the browser is idle. The
 * cookie banner is mounted separately (see ConsentBannerMount) so the
 * consent choice does not wait on the chat widget's heavier deps.
 */
const DeferredFloatingUI = () => {
  const { pathname } = useLocation();
  const ready = useIdleReady(2500);
  if (!ready || pathname === "/free-roof-estimate" || pathname === "/solar-battery-estimate" || pathname === "/shop") return null;
  return (
    <Suspense fallback={null}>
      <LiveTextWidget />
      <QuotePrompt />
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <DeferredUIProviders>
      <BrowserRouter>
        <ScrollToTop />
        <RouteProgress />
        <ConsentAwareAnalytics />
        <RouteAwareAuthProvider>
          <RouteChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route element={<PublicSiteSurface />}>
              {/* Public marketing site */}
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/our-work" element={<OurWork />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/partners/sales-organizations" element={<PartnerSalesOrg />} />
              <Route path="/partners/install-partners" element={<PartnerInstaller />} />
              <Route path="/partners/general-contractors" element={<PartnerGC />} />
              <Route path="/partners/sales-reps" element={<PartnerSalesRep />} />
              <Route path="/solar-installation" element={<SolarInstallation />} />
              <Route path="/solar-battery-estimate" element={<SolarBatteryEstimate />} />
              <Route path="/shop" element={<Shop />} />
              {/* Pillar lobbies */}
              <Route path="/solar" element={<SolarLobby />} />
              <Route path="/solar-incentives-guide-california" element={<SolarIncentivesGuideCalifornia />} />
              <Route path="/construction" element={<ConstructionLobby />} />
              <Route path="/adu-construction" element={<AduConstruction />} />
              <Route path="/home-additions" element={<HomeAdditions />} />
              <Route path="/whole-home-remodeling" element={<WholeHomeRemodeling />} />
              <Route path="/new-home-construction" element={<NewHomeConstruction />} />
              {/* Legacy → pillar redirects (Phase 2) */}
              <Route path="/energy" element={<AliasRedirect to="/solar" />} />
              <Route path="/residential-roofing" element={<AliasRedirect to="/roofing" />} />
              <Route path="/home-solutions" element={<HomeSolutions />} />
              <Route path="/roofing" element={<Roofing />} />
              <Route path="/battery-storage" element={<BatteryStorage />} />
              <Route path="/enphase" element={<Enphase />} />
              <Route path="/tesla-energy" element={<TeslaEnergy />} />
              <Route path="/tesla" element={<AliasRedirect to="/tesla-energy" />} />
              <Route path="/removal-reinstall" element={<RemovalReinstall />} />
              <Route path="/solar-service-maintenance" element={<SolarServiceMaintenance />} />
              <Route path="/solar-servicing" element={<AliasRedirect to="/solar-service-maintenance" />} />
              <Route path="/solar-maintenance" element={<AliasRedirect to="/solar-service-maintenance" />} />
              <Route path="/panel-cleaning-quote" element={<PanelCleaningQuote />} />
              <Route path="/solar-cleaning" element={<AliasRedirect to="/panel-cleaning-quote" />} />
              <Route path="/electrical" element={<Electrical />} />
             <Route path="/main-panel-upgrades" element={<MainPanelUpgrades />} />
             <Route path="/electrical-services" element={<ElectricalServices />} />
              <Route path="/ev-chargers" element={<EVChargers />} />
              <Route path="/smart-home" element={<SmartHome />} />
              <Route path="/wildfire-sprinkler" element={<WildfireSprinkler />} />
              <Route path="/general-contracting" element={<GeneralContracting />} />
              <Route path="/commercial" element={<Commercial />} />
              <Route path="/commercial-solar" element={<CommercialSolar />} />
              <Route path="/direct-pay-solar" element={<DirectPaySolar />} />
              <Route path="/commercial-roofing" element={<CommercialRoofing />} />
              <Route path="/commercial-ev-charging" element={<CommercialEVCharging />} />
              <Route path="/roof-repairs" element={<RoofRepairs />} />
               <Route path="/gutter-cleaning" element={<GutterCleaning />} />
               <Route path="/roof-cleaning" element={<RoofCleaning />} />
               <Route path="/solar-repair" element={<SolarRepair />} />
              {/* /residential-roofing handled above as a redirect to /roofing */}
              <Route path="/solar-rescue" element={<SolarRescue />} />
              <Route path="/solar-rescue/freedom-forever-help" element={<SolarRescueFreedomForever />} />
              <Route path="/solar-rescue/sunpower-help" element={<SolarRescueSunPower />} />
              <Route path="/solar-rescue/sunnova-help" element={<SolarRescueSunnova />} />
              <Route path="/solar-rescue/titan-solar-help" element={<SolarRescueTitanSolar />} />
              <Route path="/solar-rescue/adt-solar-help" element={<SolarRescueADTSolar />} />
              <Route path="/solar-rescue/petersendean-help" element={<SolarRescuePetersenDean />} />
              <Route path="/solar-rescue/installer-out-of-business" element={<InstallerOutOfBusiness />} />
              <Route path="/solar-rescue/system-takeover" element={<TakeoverRedirect />} />
              {/* Deposit configurator retired for new inquiries; historical confirmations stay below. */}
              <Route path="/solar-rescue/configure" element={<RequestRedirect category="solar-repair" />} />
              <Route path="/solar-rescue/confirmation/:token" element={<SolarRescueConfirmation />} />
              <Route path="/solar-care" element={<SolarCare />} />
              <Route path="/magic-care" element={<AliasRedirect to="/solar-care" />} />
              <Route path="/solar-system-adders" element={<SolarSystemAdders />} />
              <Route path="/solar-inspections" element={<SolarInspections />} />
              <Route path="/roof-maintenance" element={<RoofMaintenance />} />
              <Route path="/roof-inspections" element={<RoofInspections />} />
              <Route path="/get-quote" element={<GetQuote />} />
              <Route path="/start" element={<AliasRedirect to="/get-quote" />} />
              <Route path="/book-service" element={<BookService />} />
              <Route path="/booking-success" element={<BookingSuccess />} />
              <Route path="/book-service/repair-success" element={<RepairPaymentSuccess />} />
              {/* Retired roof-subtype pages — all redirect to consolidated /roofing lobby */}
              <Route path="/roofing/shingles" element={<AliasRedirect to="/roofing" />} />
              <Route path="/roofing/flat-roof" element={<AliasRedirect to="/roofing" />} />
              <Route path="/roofing/tile-reset" element={<AliasRedirect to="/roofing" />} />
              <Route path="/roofing/new-tile" element={<AliasRedirect to="/roofing" />} />
              <Route path="/roofing/new-construction" element={<AliasRedirect to="/roofing" />} />
              <Route path="/roofing/flat-tile" element={<AliasRedirect to="/roofing" />} />
              <Route path="/roofing/spanish-tile" element={<AliasRedirect to="/roofing" />} />
              <Route path="/roofing/clay-tile" element={<AliasRedirect to="/roofing" />} />
              <Route path="/roofing/metal" element={<AliasRedirect to="/roofing" />} />
              <Route path="/spanish-tile-roofing" element={<AliasRedirect to="/roofing" />} />
              <Route path="/shingles-roofing" element={<AliasRedirect to="/roofing" />} />

              <Route path="/roofing-calculator" element={<RoofingCalculator />} />
              <Route path="/free-roof-estimate" element={<FreeRoofEstimate />} />
              <Route path="/ev-calculator" element={<EVCalculatorPage />} />
              <Route path="/mpu-calculator" element={<MpuCalculatorPage />} />
              <Route path="/electrical-estimator" element={<ElectricalEstimator />} />
              <Route path="/solar-calculator" element={<SolarCalculator />} />
              {/* Draft preview only — noindex, not in sitemap, no live submissions */}
              <Route path="/energy-designer" element={<EnergyDesigner />} />
              <Route path="/removal-reinstall-calculator" element={<RemovalReinstallCalculator />} />
              {/* Unified quote dispatcher (Phase 3) */}
              <Route path="/quote" element={<QuoteDispatcher />} />
              <Route path="/packages" element={<Packages />} />
              {/* Legacy Restoration slug → new canonical Roof + Solar Restoration (preserves ?query and #hash) */}
              <Route path="/packages/restoration" element={<AliasRedirect to="/packages/roof-solar-restoration" />} />
              <Route path="/packages/restoration/build" element={<AliasRedirect to="/packages/roof-solar-restoration/build" />} />
              <Route path="/build/restoration" element={<AliasRedirect to="/packages/roof-solar-restoration/build" />} />
              <Route path="/packages/:slug" element={<PackageDetail />} />
              <Route path="/packages/:slug/build" element={<PackageBuild />} />
              {/* Smart electrical panels page + short alias */}
              <Route path="/smart-electrical-panels" element={<SmartElectricalPanels />} />
              <Route path="/smart-panels" element={<AliasRedirect to="/smart-electrical-panels" />} />
              {/* Retired BundleBuilder — known package slugs deep-link to the new builder; everything else falls back to /get-quote */}
              <Route path="/build/resilient-home" element={<AliasRedirect to="/packages/resilient-home/build" />} />
              <Route path="/build/electric-home" element={<AliasRedirect to="/packages/electric-home/build" />} />
              <Route path="/build/*" element={<AliasRedirect to="/get-quote" />} />
              <Route path="/service-areas" element={<ServiceAreas />} />
              <Route path="/coverage-map" element={<CoverageMap />} />
              <Route path="/service-areas/:service/:city" element={<CityServicePage />} />
              <Route path="/counties/:slug" element={<CountyHub />} />
              <Route path="/rebuild" element={<WildfireRebuild />} />
              <Route path="/emergency" element={<EmergencyService />} />
              <Route path="/referral" element={<ReferralProgram />} />
              <Route path="/about" element={<About />} />
              <Route path="/financing" element={<Financing />} />
              <Route path="/contact" element={<ContactPage />} />
              {/* /advisory is a legacy alias — canonical is /energy-construction-advisory */}
              <Route path="/advisory" element={<AliasRedirect to="/energy-construction-advisory" />} />
              <Route path="/energy-construction-advisory" element={<EnergyConstructionAdvisory />} />
              <Route path="/request-construction" element={<RequestConstruction />} />
              <Route path="/visualize" element={<Visualize />} />
              <Route path="/request-success" element={<RequestSuccess />} />
              <Route path="/request-electrical" element={<AliasRedirect to="/electrical-estimator" />} />



              {/* Legacy portal redirects → marketing */}
              <Route path="/portal" element={<AliasRedirect to="/get-quote" />} />
              <Route path="/portal/*" element={<AliasRedirect to="/get-quote" />} />
              <Route path="/partners/login" element={<AliasRedirect to="/partners" />} />
              <Route path="/partners/signup" element={<AliasRedirect to="/partners" />} />
              <Route path="/partners/portal/*" element={<AliasRedirect to="/partners" />} />

              {/* Editorial public resources share the customer-facing surface. */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="*" element={<NotFound />} />
              </Route>

              {/* Hosted documents and privacy preferences keep their own presentation. */}
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/q/:token" element={<QuoteView />} />
              <Route path="/quote/:token" element={<QuoteView />} />
              <Route path="/roof-quote/:token" element={<RoofQuoteToken />} />
              <Route path="/privacy-policy" element={<PublicSiteSurface><PrivacyPolicy /></PublicSiteSurface>} />

              {/* Admin panel */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/clients" element={<AdminProtectedRoute><AdminClients /></AdminProtectedRoute>} />
              <Route path="/admin/projects" element={<AdminProtectedRoute><AdminProjects /></AdminProtectedRoute>} />
              <Route path="/admin/project/:id" element={<AdminProtectedRoute><AdminProjectDetail /></AdminProtectedRoute>} />
              <Route path="/admin/invoices" element={<AdminProtectedRoute><AdminInvoices /></AdminProtectedRoute>} />
              <Route path="/admin/visualizer/:id" element={<AdminProtectedRoute><AdminVisualizerProject /></AdminProtectedRoute>} />
              <Route path="/admin/service-requests" element={<AdminProtectedRoute><AdminServiceRequests /></AdminProtectedRoute>} />
              <Route path="/admin/roof-color-requests" element={<AdminProtectedRoute><AdminRoofColorRequests /></AdminProtectedRoute>} />
              <Route path="/admin/intake-queue" element={<AdminProtectedRoute><AdminIntakeQueue /></AdminProtectedRoute>} />
              <Route path="/admin/quotes" element={<AdminProtectedRoute><AdminQuotes /></AdminProtectedRoute>} />
              <Route path="/admin/project-pins" element={<AdminProtectedRoute><AdminProjectPins /></AdminProtectedRoute>} />
              <Route path="/admin/hubspot" element={<AdminProtectedRoute><AdminHubSpot /></AdminProtectedRoute>} />
              <Route path="/admin/ai-integrations" element={<AdminProtectedRoute><AdminAiIntegrations /></AdminProtectedRoute>} />
              <Route path="/admin/ai-permissions" element={<AdminProtectedRoute><AdminAiPermissions /></AdminProtectedRoute>} />
              <Route path="/admin/audit-log" element={<AdminProtectedRoute><AdminAuditLog /></AdminProtectedRoute>} />
              <Route path="/admin/command-center" element={<AdminProtectedRoute><AdminCommandCenter /></AdminProtectedRoute>} />

              </Routes>
              <ConsentBannerMount />
              <DeferredFloatingUI />
            </Suspense>
          </RouteChunkErrorBoundary>
        </RouteAwareAuthProvider>
      </BrowserRouter>
    </DeferredUIProviders>
  </QueryClientProvider>
);

export default App;
